import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);

    if (location.pathname === "/") {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(`/#${id}`);
      // The scrolling will be handled by the Home component or a global hash handler
      // But since we are navigating to hash, native browser behavior might handle it 
      // or we can pass state to force it.
      // Let's rely on hash for now, but to be safe we can use state.
      navigate("/", { state: { scrollTo: id } });
    }
  };

  const handleNavClick = (e, id) => {
    e.preventDefault();
    scrollToSection(id);
  };

  const navLinks = [
    { name: "Features", id: "features" },
    { name: "Pricing", id: "pricing" },
    { name: "How It Works", id: "how-it-works" },
    { name: "Testimonials", id: "testimonials" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-black/80 backdrop-blur-md border-b border-white/10 py-4" : "bg-transparent py-6"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <button
          className="flex items-center gap-2 cursor-pointer bg-transparent border-none p-0 focus:outline-none focus:ring-2 focus:ring-emerald-main/50 rounded-lg"
          onClick={() => scrollToSection("hero")}
          aria-label="Scroll to top"
        >
          <div className="w-8 h-8 rounded-lg bg-emerald-main/20 flex items-center justify-center border border-emerald-main/50">
            <span className="font-bold text-emerald-main">A</span>
          </div>
          <span className="font-outfit font-bold text-xl text-white">Alice Bot</span>
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.name}
              onClick={() => scrollToSection(link.id)}
              className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
              aria-label={`Navigate to ${link.name} section`}
            >
              {link.name}
            </button>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:block">
          <a href="https://wa.me/919028230554" target="_blank" rel="noopener noreferrer">
            <Button className="bg-white/10 hover:bg-white/20 text-white rounded-full px-6 border border-white/10">
              <MessageCircle className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-black border-b border-white/10 p-6 md:hidden flex flex-col gap-4 shadow-2xl">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={`#${link.id}`}
              onClick={(e) => handleNavClick(e, link.id)}
              className="text-left text-gray-400 hover:text-white transition-colors py-2"
            >
              {link.name}
            </a>
          ))}
          <a href="https://wa.me/919028230554" target="_blank" rel="noopener noreferrer" className="w-full">
            <Button className="w-full bg-emerald-main hover:bg-emerald-light text-black font-bold rounded-full">
              <MessageCircle className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
